import { Header } from '@assets/component/Header/Header';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { useSetPageTitle, useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import { useRef } from 'react';

import { useListStorageFolder } from '@assets/hooks/useListStorageFolder';

import {
    KnowledgeBaseRefAPI,
    KnowledgeBaseTagging
} from '@assets/component/KnowldgeBaseTagging/KnowledgBaseTagging';
import {
    KnowledgeFileTree,
    KnowledgeFileTreeAPI,
    TreeEntry
} from '@assets/component/KnowledgeFileTree/KnowledgeFileTree';
import { LoadingAnim } from '@assets/component/Loading/LoadingAnim';
import { Split } from '@assets/component/Split/Split';
import {
    dbBuildKnowledgeFilePath,
    dbKnowledgeFileSanitizer,
    getFirebase
} from '@assets/firebase/FirebaseAdapater';
import { toJS } from 'mobx';
import { Button } from 'react-bootstrap';
import { IndustryTag } from '@assets/component/IndustryTagSelect/IndustryTagSelect';
import { ScrollPanel } from '@assets/component/Panel/ScrollPanel/ScrollPanel';

const KnowledgeBase = observer(() => {
    const store = useStore();

    const knowledgeFileDB = store.knowledgeFilesDB;
    const ROOT_FOLDER = 'knowledge';
    useSetPageTitle('Knowledge Base');
    const { folderContent, refreshFolder, isLoading } =
        useListStorageFolder(ROOT_FOLDER);

    const treeAPI = useRef<KnowledgeFileTreeAPI>(null);
    const taggingAPI = useRef<KnowledgeBaseRefAPI>(null);

    function flattenTree<T extends TreeEntry>(items: T[]): T[] {
        return items.flatMap(item => {
            const children = (item.children as T[]) ?? [];
            return [item, ...flattenTree(children)];
        });
    }

    const getSelectedItems = () => {
        const selectedIds = treeAPI.current.getSelectedItems();
        const treeItems = treeAPI.current.getTreeItems();
        const flatTreeItems = flattenTree(treeItems);
        const entries = selectedIds.map(id => {
            const item = flatTreeItems.find(item => item.id === id);
            return {
                id: item.id,
                path: item.path,
                type: item.type,
                tags: item.tags
            };
        });

        return entries;
    };

    const onClickDeleteSelectedRealtimeEntries = () => {
        const entries = getSelectedItems();
        const fb = getFirebase();
        entries.forEach(entry => {
            if (entry.type === 'file') {
                fb.deleteKnowledgeFileDbEntry(entry.path);
            }
        });
    };
    const onClickSave = () => {
        const entries = getSelectedItems();
        const fb = getFirebase();
        const tags = taggingAPI.current.getTags();
        entries.forEach(entry => {
            if (entry.type === 'file') {
                fb.setKnowledgeFileTags(entry.path, toJS(tags));
            }
        });

        //alert(JSON.stringify(entries, null, 2));
    };

    const onClickLoad = () => {
        const tags: IndustryTag[] = [];
        const entries = getSelectedItems();
        entries.forEach(entry => {
            if (entry.type === 'file') {
                const sanitized = dbKnowledgeFileSanitizer(entry.path);
                const entryTags = knowledgeFileDB[sanitized]?.tags ?? [];
                entryTags.forEach(tag => {
                    if (!tags.find(t => t.value === tag.value)) {
                        tags.push(tag);
                    }
                });
            }
        });

        taggingAPI.current?.loadTags(tags);
    };

    if (isLoading) {
        return <LoadingAnim />;
    }

    return (
        <>
            <Header disableTicker={true} showNewDeal={true} />
            <Split layoutPercent={[70, 30]}>
                <Flex direction="column" fillCell={true}>
                    <Flex
                        direction="row"
                        style={{ gap: '20px', padding: '10px' }}
                    >
                        <Button variant="dark" onClick={onClickSave}>
                            Save TAGS
                        </Button>
                        <Button variant="dark" onClick={onClickLoad}>
                            Load TAGS
                        </Button>

                        <Button
                            variant="danger"
                            onClick={onClickDeleteSelectedRealtimeEntries}
                        >
                            Delete Entries
                        </Button>

                        <Button
                            variant="primary"
                            onClick={() => {
                                const fb = getFirebase();
                                fb.triggerKnowledgeFilesImport();
                            }}
                        >
                            RAG Import
                        </Button>
                    </Flex>
                    <div style={{ width: '100%', height: '80%' }}>
                        <ScrollPanel>
                            <KnowledgeFileTree
                                ref={treeAPI}
                                folderContent={folderContent}
                                knowledgeFilesDB={knowledgeFileDB}
                                refreshFolder={refreshFolder}
                            />
                        </ScrollPanel>
                    </div>
                </Flex>

                <KnowledgeBaseTagging ref={taggingAPI} />
            </Split>
        </>
    );
});

export default KnowledgeBase;
